import { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { PlusIcon } from '../../assets/icons/index';
import calendar from '../../assets/images/calendar.png';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { FadeIn } from '../../components/motion/index';
import { useSelectedCompany } from '../../hooks/index';
import { StringKey } from '../../lang/index';
import { sortByToEventFormTypeMap, SortByType } from '../../types/events.types';
import { PageRoute } from '../../types/page.types';
import { AccessRole } from '../../types/stakeholder.types';
import { eventModalStore } from './desktop/desktop.store';
type EmptyEventHistoryProps = {
  isDisplayedAddEvent?: boolean;
  text?: ReactNode | string;
  title?: ReactNode | string;
  type?: SortByType;
};

const addFirstEventTranslationKey: Record<SortByType, StringKey> = {
  [SortByType.BUYBACK]: 'NO_BUYBACK_EVENTS_YET',
  [SortByType.CONVERSION]: 'NO_CONVERSION_EVENTS_YET',
  [SortByType.COMPANY_INCORPORATION]: 'NO_COMPANY_INCORPORATION_EVENTS_YET',
  [SortByType.EXERCISE]: 'NO_EXERCISE_EVENTS_YET',
  [SortByType.EXPIRE]: 'NO_EXPIRE_EVENTS_YET',
  [SortByType.FUNDRAISING_ROUND]: 'NO_FUNDRAISING_ROUND_EVENTS_YET',
  [SortByType.GRANT]: 'NO_GRANT_EVENTS_YET',
  [SortByType.NEW_PLAN]: 'NO_NEW_PLAN_EVENTS_YET',
  [SortByType.NEW_POOL]: 'NO_NEW_POOL_EVENTS_YET',
  [SortByType.PAYOUT]: 'NO_PAYOUT_EVENTS_YET',
  [SortByType.SAFE]: 'NO_SAFE_EVENTS_YET',
  [SortByType.SAFE_CONVERSION]: 'NO_SAFE_CONVERSION_EVENTS_YET',
  [SortByType.SECONDARIES]: 'NO_SECONDARIES_EVENTS_YET',
  [SortByType.SHARE_ISSUANCE]: 'NO_SHARE_ISSUANCE_EVENTS_YET',
  [SortByType.VALUATION]: 'NO_VALUATION_EVENTS_YET',
  [SortByType.APPROVAL]: 'EMPTY',
  [SortByType.DIVIDEND]: 'EMPTY',
};

const addFirstEventDescriptionTranslationKey: Record<SortByType, StringKey> = {
  [SortByType.BUYBACK]: 'NO_BUYBACK_EVENTS_DESCRIPTION',
  [SortByType.CONVERSION]: 'NO_CONVERSION_EVENTS_DESCRIPTION',
  [SortByType.COMPANY_INCORPORATION]: 'COMPANY_INCORPORATION_DESCRIPTION',
  [SortByType.EXERCISE]: 'NO_EXERCISE_EVENTS_DESCRIPTION',
  [SortByType.EXPIRE]: 'NO_EXPIRE_EVENTS_DESCRIPTION',
  [SortByType.FUNDRAISING_ROUND]: 'NO_FUNDRAISING_ROUND_EVENTS_DESCRIPTION',
  [SortByType.GRANT]: 'NO_GRANT_EVENTS_DESCRIPTION',
  [SortByType.NEW_PLAN]: 'NO_NEW_PLAN_EVENTS_DESCRIPTION',
  [SortByType.NEW_POOL]: 'NO_NEW_POOL_EVENTS_DESCRIPTION',
  [SortByType.PAYOUT]: 'NO_PAYOUT_EVENTS_DESCRIPTION',
  [SortByType.SAFE]: 'NO_SAFE_EVENTS_DESCRIPTION',
  [SortByType.SAFE_CONVERSION]: 'NO_SAFE_CONVERSION_EVENTS_DESCRIPTION',
  [SortByType.SECONDARIES]: 'NO_SECONDARIES_EVENTS_DESCRIPTION',
  [SortByType.SHARE_ISSUANCE]: 'NO_SHARE_ISSUANCE_EVENTS_DESCRIPTION',
  [SortByType.VALUATION]: 'NO_VALUATION_EVENTS_DESCRIPTION',
  [SortByType.APPROVAL]: 'EMPTY',
  [SortByType.DIVIDEND]: 'EMPTY',
};

const addFirstEventButtonTranslationKey: Record<SortByType, StringKey> = {
  [SortByType.BUYBACK]: 'ADD_BUYBACK_EVENT',
  [SortByType.CONVERSION]: 'ADD_CONVERSION_EVENT',
  [SortByType.COMPANY_INCORPORATION]: 'ADD_COMPANY_INCORPORATION',
  [SortByType.EXERCISE]: 'ADD_EXERCISE_EVENT',
  [SortByType.EXPIRE]: 'ADD_EXPIRE_EVENT',
  [SortByType.FUNDRAISING_ROUND]: 'ADD_FUNDRAISING_ROUND_EVENT',
  [SortByType.GRANT]: 'ADD_GRANT_EVENT',
  [SortByType.NEW_PLAN]: 'ADD_NEW_PLAN_EVENT',
  [SortByType.NEW_POOL]: 'ADD_NEW_POOL_EVENT',
  [SortByType.PAYOUT]: 'ADD_PAYOUT_EVENT',
  [SortByType.SAFE]: 'ADD_SAFE_EVENT',
  [SortByType.SAFE_CONVERSION]: 'ADD_SAFE_CONVERSION_EVENT',
  [SortByType.SECONDARIES]: 'ADD_SECONDARIES_EVENT',
  [SortByType.SHARE_ISSUANCE]: 'ADD_SHARE_ISSUANCE_EVENT',
  [SortByType.VALUATION]: 'ADD_VALUATION_EVENT',
  [SortByType.APPROVAL]: 'EMPTY',
  [SortByType.DIVIDEND]: 'EMPTY',
};

export const EmptyEventHistory: FC<EmptyEventHistoryProps> = ({
  isDisplayedAddEvent = true,
  text,
  title,
  type,
}) => {
  const addEventModalToggler = eventModalStore(({ setOpenState }) => setOpenState);
  const { selectedCompany } = useSelectedCompany();
  const canManageEvents =
    selectedCompany != null && selectedCompany.stakeholder?.accessRole !== AccessRole.AUDITOR;

  const navigate = useNavigate();
  const defaultOnClick = () =>
    addEventModalToggler({
      state: true,
      type: type ? sortByToEventFormTypeMap[type] : undefined,
    });

  const addFirstEventOnClickHandler: PartialRecord<SortByType, () => void> = {
    [SortByType.EXERCISE]: () => navigate(PageRoute.POOL_PLANS),
    [SortByType.EXPIRE]: () => navigate(PageRoute.POOL_PLANS),
    [SortByType.NEW_PLAN]: () => navigate(PageRoute.POOL_PLANS),
    [SortByType.NEW_POOL]: () => navigate(PageRoute.POOL_PLANS),
    [SortByType.PAYOUT]: () => navigate(PageRoute.POOL_PLANS),
    [SortByType.SAFE]: () => navigate(PageRoute.SAFES),
    [SortByType.SAFE_CONVERSION]: () => navigate(PageRoute.SAFES),
  };

  const onClickHandler = type
    ? addFirstEventOnClickHandler[type] || defaultOnClick
    : defaultOnClick;

  return (
    <FadeIn className="mb-10 flex h-fit w-fit flex-col items-center justify-center text-center">
      <div
        className="rounded-full p-[12px]"
        style={{
          boxShadow: '0px 0px 16px 0px #0985F721 inset',
        }}
      >
        <div
          className="flex size-[112px] items-center justify-center rounded-full"
          style={{
            boxShadow: '0px 0px 8px 0px #0985F721 inset',
            background:
              'linear-gradient(180deg, rgba(34, 200, 253, 0.13) 0%, rgba(3, 77, 220, 0.13) 100%)',
          }}
        >
          <img alt="calendar" className="size-14" src={calendar} />
        </div>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span
          className="!bg-clip-text text-xl font-semibold text-transparent"
          style={{
            background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
          }}
        >
          {title || (
            <AppFormattedMessage id={type ? addFirstEventTranslationKey[type] : 'NO_EVENTS'} />
          )}
        </span>
        <span className="line-clamp-2 max-w-[230px] overflow-hidden text-center text-xs font-[450] text-gray-500">
          {text ? (
            text
          ) : (
            <AppFormattedMessage
              id={type ? addFirstEventDescriptionTranslationKey[type] : 'ADD_FIRST_EVENT'}
            />
          )}
        </span>
      </div>
      {isDisplayedAddEvent &&
        canManageEvents &&
        type !== SortByType.EXERCISE &&
        type !== SortByType.EXPIRE &&
        type !== SortByType.PAYOUT &&
        type !== SortByType.SAFE &&
        type !== SortByType.SAFE_CONVERSION && (
          <Button
            className="mt-6 h-9 w-fit items-center gap-1 rounded bg-brand-700 px-4 py-[6px] text-sm font-[550] text-white"
            data-permission-required
            onClick={onClickHandler}
          >
            <PlusIcon />
            <AppFormattedMessage
              id={type ? addFirstEventButtonTranslationKey[type] : 'ADD_EVENT'}
            />
          </Button>
        )}
    </FadeIn>
  );
};
